import { WidgetType } from '../../shared/widgets/utils/widget-type';
import { KeeperType } from '../resources/keepers.resource';
import { UI_CONFIG_VERSION, UiConfig } from '../resources/ui-configs.resource';
import { AreaVisualizationConfig, GlobalVisualizationConfig } from '../resources/ui-visual-customization.resource';
import { BROWSER_LOCALE } from '../services/localization.service';
import {
  AccountPath,
  ConfigurationPath,
  ExportFilesPath,
  MapsPath,
  MonitoringPath,
  ReportsPath,
  RootPath,
  SysconfigPath,
} from './application-structure';

export const PAGE_TITLE = 'BA-UI';

export const AUTH_URL = '/BA-API';
export const BASE_URL = '/BA-API/v0.1';
export const GEOSERVER_URL = '/geoserver';

export const GLOBAL_MAP_ID = 0;

export const LOGIN_REGEX = /[a-zA-Z][\w-_.@]+/;
export const PASSWORD_REGEX = /[\w!@#$%^&*_\-+=~,;:.{}<>[\]()/'`]{6,16}/;

export enum ApiFilterOperator {
  GROUP_START = '[',
  GROUP_FINISH = ']',
  IN_SEPARATOR = '|',
  OR = ',',
  AND = ';',
  EQUALS = '==',
  NOT_EQUALS = '!=',
  CONTAINS = '=@',
  LESS = '<',
  MORE = '>',
  LESS_OR_EQUALS = '<=',
  MORE_OR_EQUALS = '>=',
  LABELED = '=#',
  LABELED_ALL = '>#',
}

export enum RtlsType {
  EMPLOYEES = 'employees',
  ITEMS = 'items',
  VEHICLES = 'vehicles',
  LABELS = 'labels',
  AREAS = 'areas',
  SITES = 'sites',
  TAGS = 'tags',
  MAPS = 'maps',
  KEEPERS = 'keepers',
  UICONFIG = 'uiconfig',
  UI_VISUAL_CUSTOMIZATION = 'ui_visual_customization',
  CUSTOM_NODES = 'custom_nodes',
  BUILDINGS = 'buildings',
  CONFIGS = 'configs',
  USERS = 'users',
}

export enum EventConfigType {
  AREA_INTERACT = 'MODULE_AREA_INTERACT',
  SPEED_EXCEED = 'MODULE_SPEED_EXCEED',
  IN_PLACE = 'MODULE_IN_PLACE',
  EMERGENCY = 'MODULE_EMERGENCY',
  SITE_DETECTOR = 'MODULE_SITE_DETECTOR',
  DANGER_ZONES = 'MODULE_DANGER_ZONES',
  UNIDENTIFIED_ACCESS = 'MODULE_UNIDENTIFIED_ACCESS',
  WRONG_ORIENTATION = 'MODULE_WRONG_ORIENTATION',
  MOTIONLESS = 'MODULE_MOTIONLESS',
  PAIRING = 'MODULE_PAIRING',
  GNSS_CONVERTER = 'MODULE_GNSS_CONVERTER',
  TAG_SOURCE_ARBITER = 'MODULE_TAG_SOURCE_ARBITER',
  GLOBAL_MAP = 'GLOBAL_MAP',
  SIZ_TYPES = 'SIZ_TYPES',
  BUILDINGS = 'MODULE_BUILDINGS',
}

export const KEEPER_EVENT_CONFIG_TYPES = [
  EventConfigType.DANGER_ZONES,
  EventConfigType.WRONG_ORIENTATION,
  EventConfigType.MOTIONLESS,
  EventConfigType.PAIRING,
  EventConfigType.IN_PLACE,
];

export const AREA_EVENT_CONFIG_TYPES = [
  EventConfigType.UNIDENTIFIED_ACCESS,
];

export const MAP_EVENT_CONFIG_TYPES = [
  EventConfigType.SITE_DETECTOR,
];

export enum ServerEvent {
  BLINK = 'BLINK',
  SITE_ENTER = 'SITE_ENTER',
  SITE_EXIT = 'SITE_EXIT',
  BUILDING_ENTER = 'BUILDING_ENTER',
  BUILDING_EXIT = 'BUILDING_EXIT',
  AREA_ENTER = 'AREA_ENTER',
  AREA_EXIT = 'AREA_EXIT',
  AREA_ENTER_EXTERNAL = 'AREA_ENTER_EXTERNAL',
  AREA_EXIT_EXTERNAL = 'AREA_EXIT_EXTERNAL',
  AREA_EMPTY = 'AREA_EMPTY',
  AREA_NONEMPTY = 'AREA_NONEMPTY',
  SPEED_EXCEED = 'SPEED_EXCEED',
  LE_UP = 'LE_UP',
  LE_DOWN = 'LE_DOWN',
  GW_DOWN = 'GW_DOWN',
  GW_UP = 'GW_UP',
  ANCHOR_UP = 'ANCHOR_UP',
  ANCHOR_DOWN = 'ANCHOR_DOWN',
  DANGER_ZONE_EXIT = 'DANGER_ZONE_EXIT',
  DANGER_ZONE_ENTER = 'DANGER_ZONE_ENTER',
  DANGER_ZONE_EMPTY = 'DANGER_ZONE_EMPTY',
  DANGER_ZONE_NONEMPTY = 'DANGER_ZONE_NONEMPTY',
  DANGER_ZONE_NONEMPTY_STATE = 'DANGER_ZONE_NONEMPTY_STATE',
  EXPORT = 'EXPORT',
  UNIDENTIFIED_ENTER = 'UNIDENTIFIED_ENTER',
  ITV_COUNTER = 'ITV_COUNTER',
  EXTERNAL = 'EXTERNAL',
  CAS360 = 'CAS360',
  WRONG_ORIENTATION = 'WRONG_ORIENTATION',
  WRONG_ORIENTATION_CANCEL = 'WRONG_ORIENTATION_CANCEL',
  MOTIONLESS = 'MOTIONLESS',
  MOTIONLESS_CANCEL = 'MOTIONLESS_CANCEL',
  NO_SIGNAL = 'NO_SIGNAL',
  ONLINE = 'ONLINE',
  BATTERY_LOW = 'BATTERY_LOW',
  FREE_FALL = 'FREE_FALL',
  PAIRING_START = 'PAIRING_START',
  PAIRING_END = 'PAIRING_END',
  IN_DIG_ON = 'IN_DIG_ON',
  IN_DIG_OFF = 'IN_DIG_OFF',
  BUTTON_PRESSED = 'BUTTON_PRESSED',
  TAG_SIGNAL_REQUEST = 'TAG_SIGNAL_REQUEST',
  TAG_ZONES_CFG = 'TAG_ZONES_CFG',
  TAG_SIZ_CFG = 'TAG_SIZ_CFG',
  TAG_EVENTS_FETCH = 'TAG_EVENTS_FETCH',
  TAG_CMD_FAILED = 'TAG_CMD_FAILED',
  TAG_CMD_COMPLETED = 'TAG_CMD_COMPLETED',
  EQUIPMENT_FAILURE = 'EQUIPMENT_FAILURE',
  EQUIPMENT_OK = 'EQUIPMENT_OK',
  CANBUS_CHANGED = 'CANBUS_CHANGED',
  TRUCKBACK_UP = 'TRUCKBACK_UP',
  TRUCKBACK_DOWN = 'TRUCKBACK_DOWN',
  ANCHOR_POWER_EXTERNAL = 'ANCHOR_POWER_EXTERNAL',
  ANCHOR_POWER_BATTERY = 'ANCHOR_POWER_BATTERY',
  TAG_UNREGISTERED = 'TAG_UNREGISTERED',
  MULTITAG_FAILURE = 'MULTITAG_FAILURE',
  MULTITAG_OK = 'MULTITAG_OK',
  OUTDOOR_ENTER = 'OUTDOOR_ENTER',
  OUTDOOR_EXIT = 'OUTDOOR_EXIT',
  LINKED = 'LINKED',
  UNLINKED = 'UNLINKED',
  ON_CHARGER = 'ON_CHARGER',
  OFF_CHARGER = 'OFF_CHARGER',
  GAS_ALARM = 'GAS_ALARM',
  GAS_ALARM_CANCEL = 'GAS_ALARM_CANCEL',
}

export const SERVER_KEEPER_EVENTS = [
  ServerEvent.AREA_ENTER,
  ServerEvent.AREA_EXIT,
  ServerEvent.SITE_ENTER,
  ServerEvent.SITE_EXIT,
  ServerEvent.BUILDING_ENTER,
  ServerEvent.BUILDING_EXIT,
  ServerEvent.SPEED_EXCEED,
  ServerEvent.DANGER_ZONE_EXIT,
  ServerEvent.DANGER_ZONE_ENTER,
  ServerEvent.DANGER_ZONE_EMPTY,
  ServerEvent.DANGER_ZONE_NONEMPTY,
  ServerEvent.DANGER_ZONE_NONEMPTY_STATE,
  ServerEvent.WRONG_ORIENTATION,
  ServerEvent.WRONG_ORIENTATION_CANCEL,
  ServerEvent.MOTIONLESS,
  ServerEvent.MOTIONLESS_CANCEL,
  ServerEvent.NO_SIGNAL,
  ServerEvent.ONLINE,
  ServerEvent.BATTERY_LOW,
  ServerEvent.FREE_FALL,
  ServerEvent.BUTTON_PRESSED,
  ServerEvent.GAS_ALARM,
  ServerEvent.GAS_ALARM_CANCEL,
  ServerEvent.EQUIPMENT_OK,
  ServerEvent.EQUIPMENT_FAILURE,
  ServerEvent.PAIRING_START,
  ServerEvent.PAIRING_END,
  ServerEvent.AREA_ENTER_EXTERNAL,
  ServerEvent.AREA_EXIT_EXTERNAL,
  ServerEvent.IN_DIG_ON,
  ServerEvent.IN_DIG_OFF,
  ServerEvent.OUTDOOR_ENTER,
  ServerEvent.OUTDOOR_EXIT,
  ServerEvent.LINKED,
  ServerEvent.UNLINKED,
];

export const SERVER_AREA_EVENTS = [
  ServerEvent.AREA_EMPTY,
  ServerEvent.AREA_NONEMPTY,
  ServerEvent.UNIDENTIFIED_ENTER,

];

export const SERVER_EXTERNAL_EVENTS = [
  ServerEvent.ITV_COUNTER,
  ServerEvent.EXTERNAL,
  ServerEvent.CAS360,
];

export type EventConfigTypeIcons = {
  [P in EventConfigType]: string;
};

export const EVENT_CONFIG_TYPE_ICONS: EventConfigTypeIcons = {
  [EventConfigType.AREA_INTERACT]: 'exit_to_app',
  [EventConfigType.SPEED_EXCEED]: 'av_timer',
  [EventConfigType.IN_PLACE]: 'blur_on',
  [EventConfigType.EMERGENCY]: 'warning',
  [EventConfigType.SITE_DETECTOR]: 'image_aspect_ratio',
  [EventConfigType.DANGER_ZONES]: 'warning',
  [EventConfigType.UNIDENTIFIED_ACCESS]: 'transfer_within_a_station',
  [EventConfigType.WRONG_ORIENTATION]: 'swap_vert',
  [EventConfigType.MOTIONLESS]: 'airline_seat_individual_suite',
  [EventConfigType.PAIRING]: 'recent_actors',
  [EventConfigType.GNSS_CONVERTER]: 'gps_fixed',
  [EventConfigType.TAG_SOURCE_ARBITER]: 'merge_type',
  [EventConfigType.GLOBAL_MAP]: 'healing',
  [EventConfigType.SIZ_TYPES]: 'local_offer',
  [EventConfigType.BUILDINGS]: 'apartment',
};

export type ServerEventIcons = {
  [P in ServerEvent]: string;
};

export const SERVER_EVENT_ICONS: ServerEventIcons = {
  [ServerEvent.AREA_ENTER]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.AREA_INTERACT],
  [ServerEvent.AREA_EXIT]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.AREA_INTERACT],
  [ServerEvent.AREA_ENTER_EXTERNAL]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.AREA_INTERACT],
  [ServerEvent.AREA_EXIT_EXTERNAL]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.AREA_INTERACT],
  [ServerEvent.AREA_EMPTY]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.AREA_INTERACT],
  [ServerEvent.AREA_NONEMPTY]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.AREA_INTERACT],
  [ServerEvent.SITE_ENTER]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.SITE_DETECTOR],
  [ServerEvent.SITE_EXIT]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.SITE_DETECTOR],
  [ServerEvent.BUILDING_ENTER]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.BUILDINGS],
  [ServerEvent.BUILDING_EXIT]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.BUILDINGS],
  [ServerEvent.BLINK]: 'settings_remote',
  [ServerEvent.SPEED_EXCEED]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.SPEED_EXCEED],
  [ServerEvent.DANGER_ZONE_ENTER]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.DANGER_ZONES],
  [ServerEvent.DANGER_ZONE_EXIT]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.DANGER_ZONES],
  [ServerEvent.DANGER_ZONE_EMPTY]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.DANGER_ZONES],
  [ServerEvent.DANGER_ZONE_NONEMPTY]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.DANGER_ZONES],
  [ServerEvent.DANGER_ZONE_NONEMPTY_STATE]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.DANGER_ZONES],
  [ServerEvent.EXPORT]: 'vertical_align_bottom',
  [ServerEvent.UNIDENTIFIED_ENTER]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.UNIDENTIFIED_ACCESS],
  [ServerEvent.WRONG_ORIENTATION]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.WRONG_ORIENTATION],
  [ServerEvent.WRONG_ORIENTATION_CANCEL]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.WRONG_ORIENTATION],
  [ServerEvent.MOTIONLESS]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.MOTIONLESS],
  [ServerEvent.MOTIONLESS_CANCEL]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.MOTIONLESS],
  [ServerEvent.NO_SIGNAL]: 'block',
  [ServerEvent.ONLINE]: 'settings_remote',
  [ServerEvent.BATTERY_LOW]: 'battery_alert',
  [ServerEvent.FREE_FALL]: 'keyboard_arrow_down',
  [ServerEvent.IN_DIG_ON]: 'settings_input_hdmi',
  [ServerEvent.IN_DIG_OFF]: 'settings_input_hdmi',
  [ServerEvent.BUTTON_PRESSED]: 'radio_button_unchecked',
  [ServerEvent.PAIRING_START]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.PAIRING],
  [ServerEvent.PAIRING_END]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.PAIRING],
  [ServerEvent.TAG_SIGNAL_REQUEST]: 'settings_remote',
  [ServerEvent.TAG_ZONES_CFG]: 'settings_remote',
  [ServerEvent.TAG_SIZ_CFG]: 'settings_remote',
  [ServerEvent.TAG_EVENTS_FETCH]: 'settings_remote',
  [ServerEvent.EQUIPMENT_FAILURE]: 'settings_remote',
  [ServerEvent.EQUIPMENT_OK]: 'settings_remote',
  [ServerEvent.ITV_COUNTER]: 'notifications',
  [ServerEvent.EXTERNAL]: 'notifications',
  [ServerEvent.CAS360]: 'my_location',
  [ServerEvent.ANCHOR_UP]: 'notifications',
  [ServerEvent.ANCHOR_DOWN]: 'notifications',
  [ServerEvent.GW_UP]: 'notifications',
  [ServerEvent.GW_DOWN]: 'notifications',
  [ServerEvent.LE_UP]: 'notifications',
  [ServerEvent.LE_DOWN]: 'notifications',
  [ServerEvent.CANBUS_CHANGED]: 'settings_input_composite',
  [ServerEvent.TRUCKBACK_UP]: 'local_shipping',
  [ServerEvent.TRUCKBACK_DOWN]: 'local_shipping',
  [ServerEvent.ANCHOR_POWER_EXTERNAL]: 'notifications',
  [ServerEvent.ANCHOR_POWER_BATTERY]: 'notifications',
  [ServerEvent.TAG_UNREGISTERED]: 'notifications',
  [ServerEvent.MULTITAG_FAILURE]: 'share',
  [ServerEvent.MULTITAG_OK]: 'share',
  [ServerEvent.TAG_CMD_FAILED]: 'notifications',
  [ServerEvent.TAG_CMD_COMPLETED]: 'notifications',
  [ServerEvent.OUTDOOR_ENTER]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.GNSS_CONVERTER],
  [ServerEvent.OUTDOOR_EXIT]: EVENT_CONFIG_TYPE_ICONS[EventConfigType.GNSS_CONVERTER],
  [ServerEvent.LINKED]: 'link',
  [ServerEvent.UNLINKED]: 'link_off',
  [ServerEvent.ON_CHARGER]: 'power',
  [ServerEvent.OFF_CHARGER]: 'power_off',
  [ServerEvent.GAS_ALARM]: 'warning',
  [ServerEvent.GAS_ALARM_CANCEL]: 'warning',
};

export enum TagDisplayStatus {
  OK = 'OK',
  IDLE = 'IDLE',
  WARNING = 'WARNING',
  BADCOORD = 'BADCOORD',
  NO_SIGNAL = 'NO_SIGNAL',
}

export enum TagBatteryStatus {
  LOW = 'LOW',
  MIDDLE = 'MID',
  GOOD = 'GOOD',
  OFF = 'OFF',
  UNDEF = 'UNDEF',
  FAIL = 'FAIL',
}

export enum AnchorEventService {
  LINK = 'LINK',
  POWER = 'POWER',
}

export enum AnchorEventStatus {
  UP = 'UP',
  DOWN = 'DOWN',
  CONFIGURE = 'CONFIGURE',
  BATTERY = 'BATTERY',
  EXTERNAL = 'EXTERNAL',
}

export enum TagEventService {
  CONFIG = 'CONFIG',
  COMMAND = 'COMMAND',
  EVENT = 'EVENT',
}

export enum TagEventStatus {
  UNREGISTERED = 'UNREGISTERED',
  MULTITAG_FAILURE = 'FAILURE',
  MULTITAG_OK = 'OK',
  ZONEIN = 'ZoneIn',
  ZONEOUT = 'ZoneOut',
  CAS_AWAY = 'CasAway',
  CAS_RED = 'CasRed',
  CAS_YELLOW = 'CasYellow',
  BUTTON_PRESSED = 'BUTTON_PRESSED',
}

export enum ReactionStatus {
  UNKNOWN = 'UNKNOWN',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  UPLOADED = 'UPLOADED',
  PENDING = 'PENDING',
  CANCELED = 'CANCELED',
  SEND = 'SEND',
}

export const TAG_COMMAND_TYPES = [
  ServerEvent.TAG_SIGNAL_REQUEST,
  ServerEvent.TAG_SIZ_CFG,
  ServerEvent.TAG_ZONES_CFG,
  ServerEvent.TAG_EVENTS_FETCH,
];

export enum CasEventType {
  ZONE_RED = 'ZoneRed',
  ZONE_YELLOW = 'ZoneYellow',
}

export const defaultVisualizationConfig: GlobalVisualizationConfig & AreaVisualizationConfig = {
  keeperIcons: {
    [KeeperType.EMPLOYEE]: {
      [TagDisplayStatus.OK]: 'img/sprites/human/green.svg',
      [TagDisplayStatus.IDLE]: 'img/sprites/human/blue.svg',
      [TagDisplayStatus.WARNING]: 'img/sprites/human/red.svg',
      [TagDisplayStatus.BADCOORD]: 'img/sprites/human/white.svg',
      [TagDisplayStatus.NO_SIGNAL]: 'img/sprites/human/gray.svg',
    },
    [KeeperType.ITEM]: {
      [TagDisplayStatus.OK]: 'img/sprites/object/purple.svg',
      [TagDisplayStatus.IDLE]: 'img/sprites/object/blue.svg',
      [TagDisplayStatus.WARNING]: 'img/sprites/object/red.svg',
      [TagDisplayStatus.BADCOORD]: 'img/sprites/object/white.svg',
      [TagDisplayStatus.NO_SIGNAL]: 'img/sprites/object/gray.svg',
    },
    [KeeperType.VEHICLE]: {
      [TagDisplayStatus.OK]: 'img/sprites/vehicle/brown.svg',
      [TagDisplayStatus.IDLE]: 'img/sprites/vehicle/blue.svg',
      [TagDisplayStatus.WARNING]: 'img/sprites/vehicle/red.svg',
      [TagDisplayStatus.BADCOORD]: 'img/sprites/vehicle/white.svg',
      [TagDisplayStatus.NO_SIGNAL]: 'img/sprites/vehicle/gray.svg',
    },
  },
  multitagIcons: {
    [TagDisplayStatus.OK]: 'img/sprites/multitag/green.svg',
    [TagDisplayStatus.IDLE]: 'img/sprites/multitag/blue.svg',
    [TagDisplayStatus.WARNING]: 'img/sprites/multitag/red.svg',
    [TagDisplayStatus.BADCOORD]: 'img/sprites/multitag/white.svg',
    [TagDisplayStatus.NO_SIGNAL]: 'img/sprites/multitag/gray.svg',
  },
  keeperColors: {
    [KeeperType.EMPLOYEE]: {
      [TagDisplayStatus.OK]: '#4CAF50',
      [TagDisplayStatus.IDLE]: '#1976D2',
      [TagDisplayStatus.WARNING]: '#D32F2F',
      [TagDisplayStatus.BADCOORD]: '#FAFAFA',
      [TagDisplayStatus.NO_SIGNAL]: '#9E9E9E',
    },
    [KeeperType.ITEM]: {
      [TagDisplayStatus.OK]: '#9C27B0',
      [TagDisplayStatus.IDLE]: '#1976D2',
      [TagDisplayStatus.WARNING]: '#D32F2F',
      [TagDisplayStatus.BADCOORD]: '#FAFAFA',
      [TagDisplayStatus.NO_SIGNAL]: '#9E9E9E',
    },
    [KeeperType.VEHICLE]: {
      [TagDisplayStatus.OK]: '#A1887F',
      [TagDisplayStatus.IDLE]: '#1976D2',
      [TagDisplayStatus.WARNING]: '#D32F2F',
      [TagDisplayStatus.BADCOORD]: '#FAFAFA',
      [TagDisplayStatus.NO_SIGNAL]: '#9E9E9E',
    },
  },
  multitagBaseColor: '#9E9E9E',
  areaColor: '#A8A8A8',
  areaDangerColor: '#CE9100',
  areaHighlightColor: '#E57373',
};

export const UI_CONFIG_DEFAULTS: UiConfig = {
  configuration: {
    pages: {
      [RootPath.MONITORING]: {
        disabled: false,
        customPages: {},
        pages: {
          [MonitoringPath.DASHBOARD]: {
            disabled: false,
            config: undefined,
          },
          [MonitoringPath.BATTERY]: {
            disabled: false,
            config: undefined,
          },
          [MonitoringPath.AREA_ATTENDANCE]: {
            disabled: false,
            config: undefined,
          },
          [MonitoringPath.EMPLOYEES]: {
            disabled: false,
            config: undefined,
          },
          [MonitoringPath.ALERTS]: {
            disabled: false,
            config: undefined,
          },
          [MonitoringPath.KEEPERS]: {
            disabled: false,
            config: undefined,
          },
          [MonitoringPath.ANCHORS]: {
            disabled: false,
            config: undefined,
          },
        },
      },
      [RootPath.MAPS]: {
        disabled: false,
        customPages: {},
        pages: {
          [MapsPath.MAP_2D]: {
            disabled: false,
            config: undefined,
          },
          [MapsPath.MAPS_SELECT]: {
            disabled: false,
            config: undefined,
          },
          [MapsPath.MAP_3D]: {
            disabled: false,
            config: undefined,
          },
        },
      },
      [RootPath.REPORTS]: {
        disabled: false,
        customPages: {},
        pages: {
          [ReportsPath.SERIES_CHART]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.KEEPER_SUMMARY]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.ACTIVITY]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.ACTIVITY_ON_SITE]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.AREAS_LOG]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.SITES_LOG]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.SITES_LOG_SUMMARY]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.BUILDINGS_LOG]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.DANGER_ZONES]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.SPEED_EXCEED]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.UNIDENTIFIED_ACCESS]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.TAG_HISTORY]: {
            disabled: false,
            config: undefined,
          },
          /*
          [ReportsPath.CONTRACTORS]: {
            disabled: false,
            config: undefined,
          },
          */
          [ReportsPath.MILEAGE]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.MOTIONLESS]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.MOTIONLESS_SUMMARY]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.CAS_EVENTS_SUMMARY]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.CAS_EVENTS_SUMMARY_SERIES]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.CAS_EVENTS]: {
            disabled: false,
            config: undefined,
          },
          /*
          [ReportsPath.SERVICE_WORK]: {
            disabled: false,
            config: undefined,
          },
          */
          [ReportsPath.AUDIT]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.AUDIT_SUMMARY]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.EVENTS]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.EXTERNAL_EVENTS]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.ANCHOR_EVENTS]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.AREA_EVENTS]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.TAGZONE_EVENTS]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.TAG_EVENTS]: {
            disabled: false,
            config: undefined,
          },
          [ReportsPath.TAG_COMMANDS]: {
            disabled: false,
            config: undefined,
          },
        },
      },
      [RootPath.EXPORT_FILES]: {
        disabled: false,
        customPages: {},
        pages: {
          [ExportFilesPath.FILES_LIST]: {
            disabled: false,
            config: undefined,
          },
          [ExportFilesPath.EXPORT_HISTORY]: {
            disabled: false,
            config: undefined,
          },
        },
      },
      [RootPath.CONFIGURATION]: {
        disabled: false,
        customPages: {},
        pages: {
          [ConfigurationPath.EMPLOYEES]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.ITEMS]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.VEHICLES]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.EQUIPMENT]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.AREAS]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.BUILDINGS]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.MAPS]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.TAGS]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.TAGS_CONFIG]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.ANCHORS]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.CUSTOM_NODES]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.USERS]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.ROLES]: {
            disabled: false,
            config: undefined,
          },
          [ConfigurationPath.LABELS]: {
            disabled: false,
            config: undefined,
          },
        },
      },
      [RootPath.SYSCONFIG]: {
        disabled: false,
        customPages: {},
        pages: {
          [SysconfigPath.EVENTS_CONFIG]: {
            disabled: false,
            config: undefined,
          },
          [SysconfigPath.SIZ_TYPES]: {
            disabled: false,
            config: undefined,
          },
          [SysconfigPath.GAS_SENSOR_SPECS]: {
            disabled: false,
            config: undefined,
          },
          [SysconfigPath.REACTIONS]: {
            disabled: false,
            config: undefined,
          },
          [SysconfigPath.REPORT_TEMPLATES]: {
            disabled: false,
            config: undefined,
          },
          [SysconfigPath.UI_CONFIG]: {
            disabled: false,
            config: undefined,
          },
          [SysconfigPath.AUDIT]: {
            disabled: false,
            config: undefined,
          },
        },
      },
      [RootPath.ACCOUNT]: {
        disabled: false,
        customPages: {},
        pages: {
          [AccountPath.PASSWORD]: {
            disabled: false,
            config: undefined,
          },
        },
      },
    },
    // contractors: [],
    employees: {
      disabled: false,
    },
    items: {
      disabled: false,
    },
    vehicles: {
      disabled: false,
    },
  },
  personalization: {
    pages: {
      [RootPath.MONITORING]: {
        pages: {
          [MonitoringPath.DASHBOARD]: {
            config: {
              updateTimeout: 15000,
              widgets: [
                WidgetType.ANCHOR_STATUS_PIE,
                WidgetType.TAG_STATUS_PIE,
                WidgetType.TAG_BATTERY_PIE,
                // WidgetType.AREAS_LOG,
                WidgetType.MAPS,
                WidgetType.CONFIGURED,
                WidgetType.AREA_ATTENDANCE,
              ],
              widgetConfigs: {
                // [WidgetType.MOTIONLESS]: {
                //   labels: [],
                //   duration: 0,
                //   report: `/${ROOT_PATH}/${RootPath.REPORTS}/${ReportsPath.MOTIONLESS}`,
                // },
              },
            },
          },
          [MonitoringPath.BATTERY]: {
            config: undefined,
          },
          [MonitoringPath.AREA_ATTENDANCE]: {
            config: undefined,
          },
          [MonitoringPath.EMPLOYEES]: {
            config: undefined,
          },
          [MonitoringPath.ALERTS]: {
            config: undefined,
          },
          [MonitoringPath.KEEPERS]: {
            config: undefined,
          },
          [MonitoringPath.ANCHORS]: {
            config: undefined,
          },
        },
      },
      [RootPath.MAPS]: {
        pages: {
          [MapsPath.MAPS_SELECT]: {
            config: {
              areasLayerTimeout: 10000,
              infrastructureLayerTimeout: 5000,
              tagsLayerTimeout: 1000,
              settingsPanelRefreshTimeout: 5000,
              sensorsRefreshTimeout: 1000,
              lastSignalStatusTimeout: 180,
              mapGroups: [],
            },
          },
          [MapsPath.MAP_2D]: {
            config: undefined,
          },
          [MapsPath.MAP_3D]: {
            config: {
              areasLayerTimeout: 10000,
              infrastructureLayerTimeout: 5000,
              tagsLayerTimeout: 1000,
            },
          },
        },
      },
      [RootPath.REPORTS]: {
        pages: {
          [ReportsPath.SERIES_CHART]: {
            config: undefined,
          },
          [ReportsPath.KEEPER_SUMMARY]: {
            config: undefined,
          },
          [ReportsPath.ACTIVITY]: {
            config: undefined,
          },
          [ReportsPath.ACTIVITY_ON_SITE]: {
            config: undefined,
          },
          [ReportsPath.AREAS_LOG]: {
            config: undefined,
          },
          [ReportsPath.SITES_LOG]: {
            config: undefined,
          },
          [ReportsPath.SITES_LOG_SUMMARY]: {
            config: undefined,
          },
          [ReportsPath.BUILDINGS_LOG]: {
            config: undefined,
          },
          [ReportsPath.DANGER_ZONES]: {
            config: undefined,
          },
          [ReportsPath.SPEED_EXCEED]: {
            config: undefined,
          },
          [ReportsPath.UNIDENTIFIED_ACCESS]: {
            config: undefined,
          },
          [ReportsPath.TAG_HISTORY]: {
            config: undefined,
          },
          /*
          [ReportsPath.CONTRACTORS]: {
            config: undefined,
          },
          */
          [ReportsPath.MILEAGE]: {
            config: undefined,
          },
          [ReportsPath.MOTIONLESS]: {
            config: undefined,
          },
          [ReportsPath.MOTIONLESS_SUMMARY]: {
            config: undefined,
          },
          [ReportsPath.CAS_EVENTS_SUMMARY]: {
            config: undefined,
          },
          [ReportsPath.CAS_EVENTS_SUMMARY_SERIES]: {
            config: undefined,
          },
          [ReportsPath.CAS_EVENTS]: {
            config: undefined,
          },
          /*
          [ReportsPath.SERVICE_WORK]: {
            config: undefined,
          },
          */
          [ReportsPath.AUDIT]: {
            config: undefined,
          },
          [ReportsPath.AUDIT_SUMMARY]: {
            config: undefined,
          },
          [ReportsPath.EVENTS]: {
            config: undefined,
          },
          [ReportsPath.TAGZONE_EVENTS]: {
            config: undefined,
          },
          [ReportsPath.ANCHOR_EVENTS]: {
            config: undefined,
          },
          [ReportsPath.TAG_EVENTS]: {
            config: undefined,
          },
          [ReportsPath.TAG_COMMANDS]: {
            config: undefined,
          },
          [ReportsPath.EXTERNAL_EVENTS]: {
            config: undefined,
          },
          [ReportsPath.AREA_EVENTS]: {
            config: undefined,
          },
        },
      },
      [RootPath.EXPORT_FILES]: {
        pages: {
          [ExportFilesPath.FILES_LIST]: {
            config: undefined,
          },
          [ExportFilesPath.EXPORT_HISTORY]: {
            config: undefined,
          },
        },
      },
      [RootPath.CONFIGURATION]: {
        pages: {
          [ConfigurationPath.EMPLOYEES]: {
            config: undefined,
          },
          [ConfigurationPath.ITEMS]: {
            config: undefined,
          },
          [ConfigurationPath.VEHICLES]: {
            config: undefined,
          },
          [ConfigurationPath.EQUIPMENT]: {
            config: {
              tags: [],
              tagSearchRefresh: 1000,
              tagSearchRefreshTimeout: 30000,
            },
          },
          [ConfigurationPath.AREAS]: {
            config: undefined,
          },
          [ConfigurationPath.BUILDINGS]: {
            config: undefined,
          },
          [ConfigurationPath.MAPS]: {
            config: undefined,
          },
          [ConfigurationPath.TAGS]: {
            config: undefined,
          },
          [ConfigurationPath.TAGS_CONFIG]: {
            config: undefined,
          },
          [ConfigurationPath.ANCHORS]: {
            config: undefined,
          },
          [ConfigurationPath.CUSTOM_NODES]: {
            config: undefined,
          },
          [ConfigurationPath.USERS]: {
            config: undefined,
          },
          [ConfigurationPath.ROLES]: {
            config: undefined,
          },
          [ConfigurationPath.LABELS]: {
            config: undefined,
          },
        },
      },
      [RootPath.SYSCONFIG]: {
        pages: {
          [SysconfigPath.EVENTS_CONFIG]: {
            config: undefined,
          },
          [SysconfigPath.SIZ_TYPES]: {
            config: undefined,
          },
          [SysconfigPath.GAS_SENSOR_SPECS]: {
            config: undefined,
          },
          [SysconfigPath.REACTIONS]: {
            config: undefined,
          },
          [SysconfigPath.REPORT_TEMPLATES]: {
            config: undefined,
          },
          [SysconfigPath.UI_CONFIG]: {
            config: undefined,
          },
          [SysconfigPath.AUDIT]: {
            config: undefined,
          },
        },
      },
      [RootPath.ACCOUNT]: {
        pages: {
          [AccountPath.PASSWORD]: {
            config: undefined,
          },
        },
      },
    },
    notifications: [],
    sortMapsByHeight: false,
    toastHide: 10000,
    notificationsRefresh: 15000,
    locale: BROWSER_LOCALE,
    uiLinkTimeBeforeEvent: 60,
    uiLinkTimeAfterEvent: 300,
    signalDuration: 10,
    signalButtonConfirm: false,
    serverTimeDelay: 0,
    areaNonEmptyLabels: [],
    hideEmptyAreas: false,
    gasHeatmapDuration: 600,
    showGasHeatmapButton: false,
    emergencyListRefresh: 5000,
    showEmergencyListButton: false,
  },
  version: UI_CONFIG_VERSION,
};
